/**
 * My Packages Page - Green & White Theme
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import CancelOrderDialog from '../components/CancelOrderDialog';
import { 
  Link2, 
  ArrowLeft,
  Package,
  MapPin,
  Clock,
  CheckCircle,
  Truck,
  ChevronRight,
  Plus,
  Navigation,
  FileText,
  Loader2,
  Zap,
  RefreshCw
} from 'lucide-react';

const statusColors = {
  created: 'bg-gray-100 text-gray-700',
  ready_for_pickup: 'bg-amber-100 text-amber-700',
  assigned: 'bg-blue-100 text-blue-700',
  picked_up: 'bg-amber-100 text-amber-700',
  in_chain: 'bg-purple-100 text-purple-700',
  in_transit: 'bg-purple-100 text-purple-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700'
};

const statusIcons = {
  created: Clock,
  ready_for_pickup: Package,
  assigned: Truck,
  picked_up: Package,
  in_chain: Truck,
  in_transit: Truck,
  delivered: CheckCircle,
  cancelled: Clock
};

const MyPackages = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resendingLabel, setResendingLabel] = useState(null);
  const [cancelPkgId, setCancelPkgId] = useState(null);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await api.get('/packages/my');
      setPackages(response.data);
    } catch (error) {
      console.error('Failed to load packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendLabel = async (pkgId) => {
    setResendingLabel(pkgId);
    try {
      const response = await api.post('/packages/' + pkgId + '/resend-label');
      if (response.data.success) {
        toast.success(response.data.message || 'Label email sent!');
        if (response.data.label_url) {
          window.open(response.data.label_url, '_blank');
        }
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to resend label';
      toast.error(msg);
    } finally {
      setResendingLabel(null);
    }
  };

  // Handle rebooking - navigate to /send with package data prefilled
  const handleRebook = (pkg) => {
    const rebookData = {
      recipient_name: pkg.recipient_name,
      recipient_phone: pkg.recipient_phone,
      recipient_email: pkg.recipient_email,
      pickup_address: pkg.pickup_address || {},
      dropoff_address: pkg.dropoff_address || {},
      package_size: pkg.package_size,
      estimated_weight_lbs: pkg.estimated_weight_lbs,
      package_contents: pkg.package_contents,
      is_fragile: pkg.is_fragile
    };
    navigate('/send', { state: { rebook: rebookData } });
    toast.success('Prefilling details from previous shipment');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-emerald-600 text-white px-6 py-4" data-testid="packages-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-white hover:bg-emerald-700 p-2">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Link2 className="h-6 w-6" />
            <span className="font-bold text-lg">My Packages</span>
          </div>
          <Button
            onClick={() => navigate('/send')}
            className="bg-white text-emerald-600 hover:bg-gray-100 rounded-full px-4"
          >
            <Plus className="h-4 w-4 mr-1" />
            New
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4" data-testid="packages-main">
        {packages.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">No Packages Yet</h2>
            <p className="text-gray-600 mb-6">Send your first package</p>
            <Button 
              onClick={() => navigate('/send')}
              className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl px-8"
            >
              Send Package
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {packages.map((pkg) => {
              const StatusIcon = statusIcons[pkg.status] || Clock;
              const hasLabel = !!pkg.label_pdf_url;
              const isPaid = pkg.payment_status === 'paid';
              const trackingRef = pkg.tracking_code || pkg.id;
              const displayId = pkg.tracking_code || (pkg.id ? pkg.id.slice(0, 8) + '...' : '');
              const price = pkg.total_cost || pkg.total_price || 0;
              const pickupCity = (pkg.pickup_address && (pkg.pickup_address.city || pkg.pickup_address.parish)) || '';
              const dropoffCity = (pkg.dropoff_address && (pkg.dropoff_address.city || pkg.dropoff_address.parish)) || '';
              const statusLabel = (pkg.status || '').replace(/_/g, ' ');
              return (
                <div
                  key={pkg.id}
                  data-testid={'package-' + pkg.id}
                  className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm hover:border-emerald-200 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={'px-2 py-1 rounded-full text-xs font-medium ' + (statusColors[pkg.status] || 'bg-gray-100 text-gray-700')}>
                          <StatusIcon className="h-3 w-3 inline mr-1" />
                          {statusLabel}
                        </span>
                        {pkg.is_urgent && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            <Zap className="h-3 w-3 inline mr-1" />
                            Urgent
                          </span>
                        )}
                      </div>
                      <p className="font-medium text-gray-800">{pkg.recipient_name}</p>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <MapPin className="h-4 w-4 text-emerald-500" />
                        <span>{pickupCity}</span>
                        <ChevronRight className="h-4 w-4" />
                        <span>{dropoffCity}</span>
                      </div>
                      <p className="text-xs text-gray-400 font-mono">{displayId}</p>
                    </div>
                    <div className="text-right space-y-2 ml-3">
                      <p className="font-bold text-emerald-600">J${price.toLocaleString()}</p>
                      <p className="text-xs text-gray-400 capitalize">{pkg.package_size}</p>
                      <div className="flex flex-col gap-1.5">
                        <Button
                          size="sm"
                          onClick={() => navigate('/track/' + trackingRef)}
                          className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs px-3"
                          data-testid={'track-btn-' + pkg.id}
                        >
                          <Navigation className="h-3 w-3 mr-1" />
                          Track
                        </Button>
                        {(hasLabel || isPaid) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResendLabel(pkg.id)}
                            disabled={resendingLabel === pkg.id}
                            className="border-blue-300 text-blue-600 hover:bg-blue-50 text-xs px-3"
                            data-testid={'resend-label-btn-' + pkg.id}
                          >
                            {resendingLabel === pkg.id ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <FileText className="h-3 w-3 mr-1" />
                            )}
                            Label
                          </Button>
                        )}
                        {/* Rebook button - always visible for delivered or paid packages */}
                        {(pkg.status === 'delivered' || isPaid) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRebook(pkg)}
                            className="border-amber-300 text-amber-600 hover:bg-amber-50 text-xs px-3"
                            data-testid={'rebook-btn-' + pkg.id}
                          >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Rebook
                          </Button>
                        )}
                        {/* Cancel button - paid, non-terminal packages */}
                        {isPaid && !['delivered', 'completed', 'cancelled', 'returned', 'failed', 'return_requested'].includes(pkg.status) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setCancelPkgId(pkg.id)}
                            className="border-red-300 text-red-600 hover:bg-red-50 text-xs px-3"
                            data-testid={'cancel-btn-' + pkg.id}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      {cancelPkgId && (
        <CancelOrderDialog
          packageId={cancelPkgId}
          onClose={() => setCancelPkgId(null)}
          onDone={() => { setCancelPkgId(null); fetchPackages(); }}
        />
      )}
    </div>
  );
};

export default MyPackages;
